import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import axios from 'axios';
import '../styles/painelOperador.css';
import '../styles/gestaoBpoRh.css';
import '../styles/fiscal.css';
import '../styles/fiscalUtilidades.css';
import '../styles/fiscalModerno.css';
import logoGerencial from '../assets/fiscal/logo_gerencial.png';
import logoComece from '../assets/fiscal/logo_comece.png';
import seloResponsabilidade from '../assets/fiscal/selo_responsabilidade_civil.png';
import seloLgpd from '../assets/fiscal/selo_lgpd.png';

// Folha timbrada: carrega as imagens já ao abrir a tela, para estarem prontas na impressão.
const IMAGENS_TIMBRADO = [logoGerencial, logoComece, seloResponsabilidade, seloLgpd];
IMAGENS_TIMBRADO.forEach((src) => { const img = new Image(); img.src = src; });

// Espera todas as imagens do documento montado (sem isso o print abre antes dos logos)
const esperarImagens = () => Promise.all(Array.from(document.querySelectorAll('#fat-impressao img')).map((img) => {
  if (img.complete && img.naturalWidth) return Promise.resolve();
  return new Promise((ok) => { img.addEventListener('load', ok, { once: true }); img.addEventListener('error', ok, { once: true }); });
}));

// Fiscal → Declaração de Faturamento (port do ZADRA). O TXT da Domínio é lido no
// servidor (matriz + filiais somadas por competência, em centavos); aqui o operador
// revisa os meses, o sócio e o local, escolhe o contador e imprime UMA folha A4.
// Nada é gravado: o CPF do sócio vive só nesta tela; o CPF completo do contador é
// buscado no clique de imprimir (com registro de acesso) e descartado depois.

const API = process.env.REACT_APP_API_BASE;
const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('access')}` });
const MESES = ['', 'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];
const CONECTIVOS = new Set(['da', 'das', 'de', 'do', 'dos', 'e']);

const brl = (centavos) => (centavos / 100).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const centavosParaCampo = (centavos) => `${Math.floor(centavos / 100)}.${String(centavos % 100).padStart(2, '0')}`;

// '1234.5' | '1234,56' -> centavos (inteiro), sem erro de ponto flutuante
const campoParaCentavos = (valor) => {
  const s = String(valor ?? '').trim().replace(',', '.');
  if (!/^\d*(\.\d*)?$/.test(s) || s === '' || s === '.') return 0;
  const [inteiro, frac = ''] = s.split('.');
  const f = `${frac}000`;
  return Number(inteiro || 0) * 100 + Number(f.slice(0, 2)) + (Number(f[2]) >= 5 ? 1 : 0);
};

const mascaraCpf = (v) => {
  const d = (v || '').replace(/\D/g, '').slice(0, 11);
  return d.replace(/^(\d{3})(\d)/, '$1.$2').replace(/^(\d{3})\.(\d{3})(\d)/, '$1.$2.$3').replace(/\.(\d{3})(\d{1,2})$/, '.$1-$2');
};

// mesma regra do servidor (fiscal_faturamento.capitalizar_nome), reaplicada antes de imprimir
const capitalizarNome = (nome) => {
  const texto = String(nome || '').trim().replace(/\s+/g, ' ').toLocaleLowerCase('pt-BR');
  return texto.split(' ').map((palavra, indice) => {
    if (indice > 0 && CONECTIVOS.has(palavra)) return palavra;
    if (/^(?:ii|iii|iv|vi|vii|viii|ix|xi|xii)$/i.test(palavra)) return palavra.toUpperCase();
    const partes = palavra.split(/([-’'])/);
    return partes.map((parte, pos) => {
      if (/^[-’']$/.test(parte)) return parte;
      if (pos > 0 && partes[pos - 1] !== '-' && parte === 'd') return parte;
      return parte ? parte.charAt(0).toLocaleUpperCase('pt-BR') + parte.slice(1) : '';
    }).join('');
  }).join(' ');
};

const acentuarCidade = (c) => c.replace(/Sao Paulo/gi, 'São Paulo').replace(/Gravatai/gi, 'Gravataí').replace(/Florianopolis/gi, 'Florianópolis');

const dataPorExtenso = (cidade) => {
  const d = new Date();
  // como no modelo da Gerencial: só a cidade ("Porto Alegre, RS" → "Porto Alegre")
  const soCidade = cidade.trim().replace(/,\s*[A-Za-z]{2}$/, '');
  return `${acentuarCidade(soCidade)}, ${String(d.getDate()).padStart(2, '0')} de ${MESES[d.getMonth() + 1]} de ${d.getFullYear()}`;
};

function Assinatura({ nome, linhas }) {
  return (
    <div className="assinatura-item">
      <p>___________________________________</p>
      <p><strong>{nome}</strong></p>
      <p>{linhas.map((l, i) => <React.Fragment key={i}>{i > 0 && <br />}{l}</React.Fragment>)}</p>
    </div>
  );
}

function DocumentoImpressao({ doc }) {
  return createPortal(
    <div id="fat-impressao" aria-hidden="true">
      <div className="print-header">
        <img src={logoGerencial} alt="Gerencial" />
        <img src={logoComece} alt="Comece" />
      </div>
      <hr className="print-separator" />
      <h2 className="titulo-doc">DECLARAÇÃO DE FATURAMENTO</h2>
      <p className="texto-doc texto-declaracao">
        Declaramos a quem possa interessar que a empresa <strong>{doc.empresa}</strong>, inscrita no CNPJ sob <strong>{doc.cnpj}</strong>, teve a seguinte receita bruta no período abaixo:
      </p>
      <div className="texto-doc tabela-wrap">
        <table className="tabela-faturamento">
          <tbody>
            {doc.periodos.map((p) => (
              <tr key={`${p.ano}-${p.mes}`}>
                <td className="coluna-mes">{MESES[p.mes]}</td>
                <td className="coluna-ano">{p.ano}</td>
                <td className="coluna-valor">R$ {brl(p.valor_centavos)}</td>
              </tr>
            ))}
            <tr className="linha-total">
              <td className="coluna-mes">Total</td>
              <td></td>
              <td className="coluna-valor">R$ {brl(doc.total)}</td>
            </tr>
          </tbody>
        </table>
      </div>
      <p className="texto-doc data-local">{doc.dataLocal}</p>
      <div className="texto-doc bloco-assinaturas">
        <div className="assinaturas-grid">
          {doc.socio && <Assinatura nome={doc.socio.nome} linhas={['Sócio(a)', `CPF: ${doc.socio.cpf}`]} />}
          <Assinatura nome={doc.contador.nome}
            linhas={['CONTADOR', `CPF: ${doc.contador.cpf}`, `CRC/${doc.contador.crc_uf}: ${doc.contador.crc}`]} />
        </div>
      </div>
      <div className="print-footer texto-doc">
        <div className="print-footer-linha">
          <div>
            Porto Alegre/RS -Rua Barbedo, 250, 3° andar, sala 316 <br />
            Florianópolis/SC - R. Hermann Blumenau, 110, 1° andar, sala 14<br /><br />
            Gerencial Auditoria e Consultoria S/S |  Conselho Regional de Contabilidade: CRC/RS 2.257<br />
            Conselho Regional de Economia: CORECON/RS 193
          </div>
          <div className="print-footer-logos">
            <img src={seloResponsabilidade} alt="Selo Responsabilidade Civil" />
            <img src={seloLgpd} alt="Selo LGPD" />
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}

const VAZIO = { empresa: '', cnpj: '', socio: '', socioCpf: '', cidade: '' };

export default function FiscalFaturamento() {
  const [dados, setDados] = useState(VAZIO);
  const [periodos, setPeriodos] = useState([]);      // [{ mes, ano, valor: string }]
  const [importado, setImportado] = useState(null);  // { arquivo, estabelecimentos, empresa_encontrada }
  const [incluirSocio, setIncluirSocio] = useState(false);
  const [contadores, setContadores] = useState([]);
  const [contadorId, setContadorId] = useState('');
  const [semCadastro, setSemCadastro] = useState(false);
  const [lendo, setLendo] = useState(false);
  const [arrastando, setArrastando] = useState(false);
  const [imprimindo, setImprimindo] = useState(false);
  const [documento, setDocumento] = useState(null);
  const [erro, setErro] = useState('');
  const [msg, setMsg] = useState('');
  const entrada = useRef(null);
  const sequencia = useRef(0);

  useEffect(() => {
    const opcoes = { params: { _: Date.now() }, headers: headers() };
    Promise.all([
      axios.get(`${API}/api/fiscal/contadores/`, opcoes),
      axios.get(`${API}/api/fiscal/contador/meu/`, opcoes).catch(() => null),
    ]).then(([lista, meu]) => {
      const todos = lista.data.contadores || [];
      setContadores(todos);
      const eu = meu?.data?.contador;
      setSemCadastro(!eu);
      if (eu) {
        const proprio = todos.find((c) => c.nome === eu.nome && c.crc === eu.crc);
        if (proprio) setContadorId(String(proprio.user_id));
      }
    }).catch((e) => setErro(e.response?.data?.mensagem || 'Não foi possível carregar os contadores'));
  }, []);

  // sai do modo impressão e descarta os dados do documento (CPF do contador incluso)
  const limparImpressao = useCallback(() => {
    document.documentElement.classList.remove('fat-imprimindo');
    document.body.classList.remove('fat-imprimindo');
    setDocumento(null);
  }, []);
  useEffect(() => {
    window.addEventListener('afterprint', limparImpressao);
    return () => { window.removeEventListener('afterprint', limparImpressao); limparImpressao(); };
  }, [limparImpressao]);

  // com o documento montado no body, imprime depois do próximo quadro (e das fontes)
  useEffect(() => {
    if (!documento) return;
    document.documentElement.classList.add('fat-imprimindo');
    document.body.classList.add('fat-imprimindo');
    const abrir = () => requestAnimationFrame(() => requestAnimationFrame(() => {
      try { window.print(); } finally { setTimeout(limparImpressao, 0); }
    }));
    // fontes e imagens da folha timbrada carregadas antes de abrir a impressão
    Promise.all([document.fonts?.ready || Promise.resolve(), esperarImagens()]).then(abrir, abrir);
  }, [documento, limparImpressao]);

  const limpar = () => { setDados(VAZIO); setPeriodos([]); setImportado(null); setIncluirSocio(false); };

  const importar = (arquivo) => {
    if (!arquivo) return;
    const tentativa = ++sequencia.current;
    limpar(); setErro(''); setMsg('');
    if (!/\.txt$/i.test(arquivo.name || '')) { setErro('Selecione um arquivo TXT exportado pela Domínio.'); return; }
    const fd = new FormData();
    fd.append('arquivo', arquivo);
    setLendo(true);
    axios.post(`${API}/api/fiscal/faturamento/importar/`, fd, { headers: headers() })
      .then((r) => {
        if (tentativa !== sequencia.current) return;
        const d = r.data;
        setDados({ empresa: d.empresa, cnpj: d.cnpj, socio: d.socio, socioCpf: d.socio_cpf, cidade: d.cidade });
        setPeriodos(d.periodos.map((p) => ({ mes: p.mes, ano: p.ano, valor: centavosParaCampo(p.valor_centavos) })));
        setImportado({ arquivo: d.arquivo, estabelecimentos: d.estabelecimentos, empresa_encontrada: d.empresa_encontrada });
        const est = d.estabelecimentos === 1 ? 'estabelecimento' : 'estabelecimentos';
        const comp = d.periodos.length === 1 ? 'competência' : 'competências';
        setMsg(`Relatório consolidado — ${d.arquivo}: ${d.estabelecimentos} ${est}, ${d.periodos.length} ${comp} e R$ ${brl(d.total_centavos)}.`);
      })
      .catch((e) => { if (tentativa === sequencia.current) setErro(e.response?.data?.mensagem || 'Não foi possível ler o arquivo selecionado.'); })
      .finally(() => { if (tentativa === sequencia.current) setLendo(false); });
  };

  const totalCentavos = useMemo(() => periodos.reduce((s, p) => s + campoParaCentavos(p.valor), 0), [periodos]);

  const imprimir = () => {
    setErro('');
    if (!dados.cidade.trim()) { setErro('Informe o local da emissão antes de imprimir.'); return; }
    if (!contadorId) { setErro('Escolha o contador que assina a declaração.'); return; }
    const nomeSocio = capitalizarNome(dados.socio);
    if (incluirSocio) {
      if (!nomeSocio) { setErro('Informe o nome do sócio para incluir a assinatura.'); return; }
      if (dados.socioCpf.replace(/\D/g, '').length !== 11) { setErro('Informe o CPF do sócio (11 dígitos) para incluir a assinatura.'); return; }
    }
    setDados((d) => ({ ...d, socio: nomeSocio }));
    setImprimindo(true);
    axios.post(`${API}/api/fiscal/faturamento/contador/${contadorId}/`, {}, { headers: headers() })
      .then((r) => {
        const lista = [...periodos]
          .sort((a, b) => (a.ano - b.ano) || (a.mes - b.mes))
          .map((p) => ({ mes: p.mes, ano: p.ano, valor_centavos: campoParaCentavos(p.valor) }));
        setDocumento({
          empresa: dados.empresa,
          cnpj: dados.cnpj,
          periodos: lista,
          total: lista.reduce((s, p) => s + p.valor_centavos, 0),
          dataLocal: dataPorExtenso(dados.cidade),
          contador: r.data.contador,
          socio: incluirSocio ? { nome: nomeSocio, cpf: mascaraCpf(dados.socioCpf) } : null,
        });
      })
      .catch((e) => setErro(e.response?.data?.mensagem || 'Não foi possível carregar os dados do contador'))
      .finally(() => setImprimindo(false));
  };

  const soltar = (ev) => { ev.preventDefault(); setArrastando(false); importar(ev.dataTransfer.files[0]); };

  return (
    <div className="painel-op fz-app">
      <div className="container-fluid mt-4 shadow p-4 rounded bg-white">
        <div className="header-wrapper d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
          <div className="d-flex align-items-center">
            <i className="fas fa-file-invoice-dollar header-icon me-2" aria-hidden="true"></i>
            <h2 className="titulo-pagina m-0">Declaração de Faturamento</h2>
          </div>
        </div>

        <div className="alert alert-light border small py-2">
          <i className="fas fa-circle-info me-2 text-primary"></i>
          Importe o <b>TXT da Domínio</b>, confira os dados consolidados (matriz e filiais somadas por competência) e gere a declaração em <b>uma folha A4</b>.
          Nada do arquivo fica gravado no Saber.
        </div>
        {erro && <div className="alert alert-danger py-2" role="button" onClick={() => setErro('')}><i className="fas fa-circle-exclamation me-2"></i>{erro}</div>}
        {msg && <div className="alert alert-success py-2" role="status"><i className="fas fa-circle-check me-2"></i>{msg}</div>}

        <div className="fat-grid">
          <section className="po-card">
            <header className="po-card-head"><h3><span className="fat-etapa">1</span>Importar relatório</h3></header>
            <div className="po-card-body">
              <div className={`fat-drop ${arrastando ? 'arrastando' : ''}`} role="button" tabIndex={0}
                onClick={() => entrada.current?.click()}
                onKeyDown={(ev) => { if (ev.key === 'Enter' || ev.key === ' ') { ev.preventDefault(); entrada.current?.click(); } }}
                onDragEnter={(ev) => { ev.preventDefault(); setArrastando(true); }}
                onDragOver={(ev) => { ev.preventDefault(); ev.dataTransfer.dropEffect = 'copy'; setArrastando(true); }}
                onDragLeave={(ev) => { if (!ev.currentTarget.contains(ev.relatedTarget)) setArrastando(false); }}
                onDrop={soltar}>
                <i className={`fas ${lendo ? 'fa-spinner fa-spin' : 'fa-cloud-arrow-up'} fat-drop-ico`} aria-hidden="true"></i>
                <div className="fw-bold">{lendo ? 'Lendo o arquivo...' : 'Arraste o TXT da Domínio aqui'}</div>
                <div className="fz-sub">ou clique para selecionar</div>
                {importado && <div className="fz-sub fz-mono">{importado.arquivo}</div>}
              </div>
              <input ref={entrada} type="file" accept=".txt,text/plain" className="d-none"
                onChange={(ev) => { importar(ev.target.files[0]); ev.target.value = ''; }} />
            </div>
          </section>

          <section className="po-card">
            <header className="po-card-head"><h3><span className="fat-etapa">2</span>Revisão dos dados</h3></header>
            <div className="po-card-body">
              <div className="row g-2">
                <div className="col-md-8">
                  <label className="form-label small fw-bold mb-1" htmlFor="fat-empresa">Empresa</label>
                  <input id="fat-empresa" className="form-control" value={dados.empresa} readOnly />
                  {importado && !importado.empresa_encontrada && <div className="small text-danger mt-1">CNPJ não encontrado na Base Fiscal — confira o cadastro antes de imprimir.</div>}
                </div>
                <div className="col-md-4">
                  <label className="form-label small fw-bold mb-1" htmlFor="fat-cnpj">CNPJ</label>
                  <input id="fat-cnpj" className="form-control fz-mono" value={dados.cnpj} readOnly />
                </div>
                <div className="col-md-8">
                  <label className="form-label small fw-bold mb-1" htmlFor="fat-socio">Sócio administrador</label>
                  <input id="fat-socio" className="form-control" value={dados.socio} disabled={!importado}
                    onChange={(ev) => setDados((d) => ({ ...d, socio: ev.target.value }))}
                    onBlur={() => setDados((d) => ({ ...d, socio: capitalizarNome(d.socio) }))} />
                </div>
                <div className="col-md-4">
                  <label className="form-label small fw-bold mb-1" htmlFor="fat-socio-cpf">CPF do sócio</label>
                  <input id="fat-socio-cpf" className="form-control fz-mono" placeholder="000.000.000-00" autoComplete="off" value={dados.socioCpf} disabled={!importado}
                    onChange={(ev) => setDados((d) => ({ ...d, socioCpf: mascaraCpf(ev.target.value) }))} />
                </div>
                <div className="col-12">
                  <div className="form-check form-switch">
                    <input className="form-check-input" type="checkbox" id="fat-assinatura-socio" checked={incluirSocio} disabled={!importado}
                      onChange={(ev) => setIncluirSocio(ev.target.checked)} />
                    <label className="form-check-label small" htmlFor="fat-assinatura-socio">Incluir assinatura do sócio ao lado da assinatura do contador</label>
                  </div>
                </div>
                <div className="col-md-6">
                  <label className="form-label small fw-bold mb-1" htmlFor="fat-cidade">Local da emissão</label>
                  <input id="fat-cidade" className="form-control" value={dados.cidade} disabled={!importado}
                    onChange={(ev) => setDados((d) => ({ ...d, cidade: ev.target.value }))} />
                </div>
                <div className="col-md-6">
                  <label className="form-label small fw-bold mb-1" htmlFor="fat-contador">Contador que assina</label>
                  <select id="fat-contador" className="form-select" value={contadorId} onChange={(ev) => setContadorId(ev.target.value)}>
                    <option value="">Selecione o contador</option>
                    {contadores.map((c) => <option key={c.user_id} value={c.user_id}>{c.nome} — CRC/{c.crc_uf} {c.crc}</option>)}
                  </select>
                  {semCadastro && (
                    <div className="small text-muted mt-1">
                      Você não tem cadastro de contador. <Link to="/Fiscal/Configuracoes">Cadastrar em Configurações</Link>
                    </div>
                  )}
                </div>
              </div>
              <button type="button" className="btn btn-primary w-100 mt-3" onClick={imprimir} disabled={!importado || imprimindo || lendo}>
                <i className={`fas ${imprimindo ? 'fa-spinner fa-spin' : 'fa-print'} me-2`}></i>Gerar e imprimir declaração
              </button>
            </div>
          </section>
        </div>

        {importado && (
          <section className="po-card mt-3">
            <header className="po-card-head">
              <h3><span className="fat-etapa">3</span>Faturamento mensal consolidado</h3>
              <span className="badge bg-light text-dark">{periodos.length}</span>
            </header>
            <div className="po-card-body">
              <p className="fz-sub mb-3">Os valores de matriz e filiais já aparecem somados por competência. Ajuste só os meses que precisarem de correção — meses em zero continuam na declaração.</p>
              <div className="fat-valores">
                {[...periodos].sort((a, b) => (a.ano - b.ano) || (a.mes - b.mes)).map((p) => {
                  const id = `fat-valor-${p.ano}-${p.mes}`;
                  const rotulo = `${MESES[p.mes].substring(0, 3)}/${p.ano}`;
                  return (
                    <div key={id}>
                      <label htmlFor={id} className="d-block">{rotulo}</label>
                      <input id={id} type="number" step="0.01" min="0" inputMode="decimal" className="form-control form-control-sm fz-num"
                        value={p.valor} aria-label={`Faturamento de ${rotulo}`}
                        onChange={(ev) => setPeriodos((lista) => lista.map((x) => (x.mes === p.mes && x.ano === p.ano ? { ...x, valor: ev.target.value } : x)))} />
                    </div>
                  );
                })}
              </div>
              <div className="fat-total">Total: R$ {brl(totalCentavos)}</div>
            </div>
          </section>
        )}
      </div>
      {documento && <DocumentoImpressao doc={documento} />}
    </div>
  );
}
